import axios from "axios";
import { api } from "./api_route";

//Case Billings

// =========== GET ALL BILLINGS BY CASE ID ===========
export const getAllBillingsByCaseId = async (caseDetailsID) => {
    try {
        const response = await axios.get(
            `${api}/case/caseBillings/all/${caseDetailsID}`
        );

        return {
            success: true,
            data: response.data.data || [],
            count: response.data.count || 0,
            message: response.data.message || "Billings fetched successfully",
        };
    } catch (error) {
        console.error("Error fetching billings:", error);
        return {
            success: false,
            data: [],
            count: 0,
            message: error.response?.data?.message || "Failed to fetch billings",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== GET SINGLE BILLING BY ID ===========
export const getBillingById = async (billingID) => {
    try {
        const response = await axios.get(
            `${api}/case/caseBillings/${billingID}`
        );

        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Billing fetched successfully",
        };
    } catch (error) {
        console.error("Error fetching billing:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to fetch billing",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== CREATE NEW BILLING ===========
export const createBilling = async (billingData) => {
    try {
        const response = await axios.post(
            `${api}/case/caseBillings`,
            billingData
        );

        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Billing created successfully",
        };
    } catch (error) {
        console.error("Error creating billing:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to create billing",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== UPDATE BILLING (GENERIC) ===========
export const updateBilling = async (billingID, billingData) => {
    try {
        const response = await axios.put(
            `${api}/case/caseBillings/${billingID}`,
            billingData
        );

        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Billing updated successfully",
        };
    } catch (error) {
        console.error("Error updating billing:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to update billing",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== UPDATE AGREED FEES ===========
export const updateAgreedFees = async (billingID, data) => {
    try {
        const response = await axios.put(
            `${api}/case/caseBillings/agreed-fees/${billingID}`,
            data
        );
        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Agreed fees updated successfully",
        };
    } catch (error) {
        console.error("Error updating agreed fees:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to update agreed fees",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== UPDATE INITIAL BILL ===========
export const updateInitialBill = async (billingID, data) => {
    try {
        const response = await axios.put(
            `${api}/case/caseBillings/initial-bill/${billingID}`,
            data
        );
        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Initial bill updated successfully",
        };
    } catch (error) {
        console.error("Error updating initial bill:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to update initial bill",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== UPDATE FINAL BILL ===========
export const updateFinalBill = async (billingID, data) => {
    try {
        const response = await axios.put(
            `${api}/case/caseBillings/final-bill/${billingID}`,
            data
        );
        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Final bill updated successfully",
        };
    } catch (error) {
        console.error("Error updating final bill:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to update final bill",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== UPDATE RECONCILIATION ===========
export const updateReconciliation = async (billingID, data) => {
    try {
        const response = await axios.put(
            `${api}/case/caseBillings/reconciliation/${billingID}`,
            data
        );
        return {
            success: true,
            data: response.data.data,
            message: response.data.message || "Reconciliation updated successfully",
        };
    } catch (error) {
        console.error("Error updating reconciliation:", error);
        return {
            success: false,
            data: null,
            message: error.response?.data?.message || "Failed to update reconciliation",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== DELETE BILLING ===========
export const deleteBilling = async (billingID, updatedBy) => {
    try {
        const response = await axios.delete(
            `${api}/case/caseBillings/${billingID}`,
            { data: { updatedBy } }
        );

        return {
            success: true,
            message: response.data.message || "Billing deleted successfully",
        };
    } catch (error) {
        console.error("Error deleting billing:", error);
        return {
            success: false,
            message: error.response?.data?.message || "Failed to delete billing",
            error: error.response?.data?.error || error.message,
        };
    }
};

// =========== HELPER FUNCTIONS ===========

export const formatBillingData = (data) => {
    const formatted = { ...data };


    if (Array.isArray(formatted.attachBillsInvoices)) {
        formatted.attachBillsInvoices = formatted.attachBillsInvoices.join(",");
    }

    const formatDate = (date) => {
        if (!date) return null;
        if (date instanceof Date) {
            return date.toISOString().split("T")[0];
        }
        return date;
    };

    if (formatted.caseDetailsID && !formatted.caseRowID) {
        formatted.caseRowID = formatted.caseDetailsID;
    }


    formatted.initialFeeProvisionPeriod = formatDate(formatted.initialFeeProvisionPeriod);
    formatted.initialBillingPeriod = formatDate(formatted.initialBillingPeriod);
    formatted.initialBillDate = formatDate(formatted.initialBillDate);
    formatted.initialBillSentOn = formatDate(formatted.initialBillSentOn);

    formatted.finalFeeProvisionPeriod = formatDate(formatted.finalFeeProvisionPeriod);
    formatted.finalBillingPeriod = formatDate(formatted.finalBillingPeriod);
    formatted.finalBillDate = formatDate(formatted.finalBillDate);
    formatted.finalBillSentOn = formatDate(formatted.finalBillSentOn);

    formatted.accountsVerificationDate = formatDate(formatted.accountsVerificationDate);

    return formatted;
};

export const parseInvoiceIds = (invoiceField) => {
    if (!invoiceField) {
        return [];
    }
    if (Array.isArray(invoiceField)) {
        return invoiceField;
    }
    if (typeof invoiceField !== 'string') {
        return [];
    }
    if (invoiceField.trim() === "") {
        return [];
    }
    return invoiceField
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id !== "");
};

export const validateBillingData = (data) => {
    const errors = {};

    if (!data.caseDetailsID) {
        errors.caseDetailsID = "Case ID is required";
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};



export const billingsDocumentDownload = async (values) => {
    try {
        const response = await axios.get(`${api}/documents/Billings/${values}`, {
            responseType: "blob",
        });
        return response.data;
    } catch (error) {
        return null;
    }
};
